import { useState } from 'react';
import { Button, Input } from "@repo/o37-ui";
import { supabase } from "../supabaseClient";

export default function MFASetupFlow({ onComplete }: { onComplete: () => void }) {
  const [step, setStep] = useState<'intro' | 'qr' | 'verify'>('intro');
  const [factorId, setFactorId] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const startSetup = async () => {
    try {
      setLoading(true);
      setError('');
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Authenticator App'
      });
      
      if (error) throw error;

      if (data) {
        setFactorId(data.id);
        setQrCode(data.totp.qr_code);
        setSecret(data.totp.secret);
        setStep('qr');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to start MFA setup');
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    try {
      setLoading(true);
      setError('');
      
      const { error } = await supabase.auth.mfa.challengeAndVerify({
        factorId: factorId,
        code: verifyCode
      });
      
      if (error) throw error;

      onComplete();
    } catch (e: any) {
      setError(e.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'intro') {
    return (
      <div className="space-y-4">
        <p className="text-sm text-neutral-600">
          Add an extra layer of security to your account by requiring a verification code from an authenticator app when you log in.
        </p>
        <Button onClick={startSetup} disabled={loading} text={loading ? "Starting..." : "Setup 2FA"} />
        {error && <div className="text-red-600 text-sm">{error}</div>}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="font-medium text-neutral-900">Scan QR Code</h3>
        <p className="text-sm text-neutral-600">
          Open your authenticator app (like Google Authenticator or Authy) and scan the QR code below.
        </p>
        
        <div className="flex justify-center bg-white p-4 border rounded-lg">
           {qrCode && <img src={qrCode} alt="QR Code" className="w-48 h-48" />}
        </div>
        
        <div className="text-center">
            <p className="text-xs text-neutral-500 mb-1">Unable to scan?</p>
            <code className="bg-neutral-100 px-2 py-1 rounded text-xs select-all">{secret}</code>
        </div>
      </div>

      <div className="space-y-4 border-t pt-4">
        <h3 className="font-medium text-neutral-900">Verify Code</h3>
        <p className="text-sm text-neutral-600">
          Enter the 6-digit code from your authenticator app to confirm setup.
        </p>
        
        <div className="flex gap-2">
            <Input 
                value={verifyCode}
                onChange={e => setVerifyCode(e.target.value)}
                placeholder="000000"
                className="max-w-[200px] text-center tracking-widest font-mono text-lg"
                maxLength={6}
            />
            <Button onClick={verifyAndEnable} disabled={loading || verifyCode.length !== 6} text={loading ? "Verifying..." : "Enable 2FA"} />
        </div>
        
        {error && <div className="text-red-600 text-sm">{error}</div>}
      </div>
      
      <button onClick={() => setStep('intro')} className="text-xs text-neutral-400 hover:text-neutral-600">
        Cancel Setup
      </button>
    </div>
  );
}
